import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileBarChart, RefreshCw, Clock, PackagePlus, Printer, Tag, Download, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getJobberReport, getJobOrder, getBundleUsns, forceCloseJobOrder } from '@/services/inhouseJobOrderApi';
import UsnStickerDialog from '@/components/print/UsnStickerDialog';
import { JwEwbCell, JwTransferPrintBtn, useJwEwb } from '@/components/jobWork/JwTransfer';

const isAdminRole = () => {
  let u;
  try { u = JSON.parse(localStorage.getItem('user') || '{}'); } catch { u = {}; }
  const r = (u.role || localStorage.getItem('role') || '').toString().trim().toLowerCase();
  return ['admin', 'super_admin', 'site admin', 'site_admin'].includes(r);
};

const fmtAge = (h) => {
  if (h == null) return '—';
  if (h < 24) return `${h.toFixed(0)}h`;
  const d = Math.floor(h / 24);
  const rem = Math.round(h - d * 24);
  return rem ? `${d}d ${rem}h` : `${d}d`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const dayKey = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const statusClass = (s) => {
  if (s === 'COMPLETED') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (s === 'RECEIVING') return 'bg-blue-100 text-blue-700 border-blue-200';
  if (s === 'OPEN') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (s === 'FORCE_CLOSED') return 'bg-purple-100 text-purple-700 border-purple-200';
  if (s === 'CANCELLED') return 'bg-gray-100 text-gray-500 border-gray-200';
  return 'bg-gray-100 text-gray-600 border-gray-200';
};

const COL_COUNT = 17;

const JobberReport = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [ewb] = useJwEwb('job_order', rows.map((o) => o.jw_id));
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [stickerJw, setStickerJw] = useState(null);
  const [fcTarget, setFcTarget] = useState(null);
  const [fcReason, setFcReason] = useState('');
  const [fcSubmitting, setFcSubmitting] = useState(false);
  const admin = isAdminRole();

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const data = await getJobberReport(params);
      setRows(data.orders || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const buildSlipHtml = (o) => {
    const t = o.target || {};
    const created = o.created_at ? new Date(o.created_at).toLocaleString('en-IN') : '—';
    const rows = (o.bundles || []).map((b, i) => `
      <tr>
        <td style="text-align:center">${i + 1}</td>
        <td style="font-family:monospace">${esc(b.bundle_code)}</td>
        <td>${esc(b.design_name || '—')}</td>
        <td>${esc(b.shade || '—')}</td>
        <td style="text-align:right">${b.qty ?? 0}</td>
      </tr>`).join('');
    return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(o.jw_id)}</title>
      <style>
        @page{size:A4;margin:14mm}
        *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#1A1F2E;font-size:12px}
        h1{font-size:18px;margin:0} .sub{color:#666;font-size:11px;margin-top:2px}
        .hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1A1F2E;padding-bottom:8px;margin-bottom:12px}
        .meta{display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;margin-bottom:14px}
        .meta div{padding:3px 0;border-bottom:1px dotted #ccc}
        .lbl{color:#666} .val{font-weight:bold}
        table{width:100%;border-collapse:collapse;margin-top:6px}
        th,td{border:1px solid #bbb;padding:6px 8px;font-size:11px}
        th{background:#f1f1f1;text-align:left}
        tfoot td{font-weight:bold;background:#fafafa}
        .sign{display:flex;justify-content:space-between;margin-top:40px}
        .sign div{width:40%;border-top:1px solid #333;padding-top:4px;text-align:center;color:#555}
      </style></head><body onload="window.print()">
      <div class="hdr">
        <div><h1>Job Work Order</h1><div class="sub">Inhouse — Jobber Dispatch Slip</div></div>
        <div style="text-align:right"><div class="val" style="font-size:15px">${esc(o.jw_id)}</div><div class="sub">${esc(created)}</div></div>
      </div>
      <div class="meta">
        <div><span class="lbl">Jobber: </span><span class="val">${esc(o.jobber_name || '—')}</span></div>
        <div><span class="lbl">Status: </span><span class="val">${esc(o.status || '—')}</span></div>
        <div><span class="lbl">Target Design: </span><span class="val">${esc(t.design_name || '—')}</span></div>
        <div><span class="lbl">SKU: </span><span class="val">${esc(t.sku_code || '—')}</span></div>
        <div><span class="lbl">Shade: </span><span class="val">${esc(t.shade || '—')}</span></div>
        <div><span class="lbl">Size: </span><span class="val">${esc(t.size || '—')}</span></div>
        <div><span class="lbl">Description: </span><span class="val">${esc(t.description || '—')}</span></div>
        <div><span class="lbl">Total Sent Qty: </span><span class="val">${o.sent_qty ?? 0}</span></div>
        <div><span class="lbl">E-Way Bill No: </span><span class="val">${esc(ewb[o.jw_id]?.ewb_no || ewb[o.jw_id]?.status_label || '—')}</span></div>
        <div><span class="lbl">E-way bill doc no: </span><span class="val">${esc(ewb[o.jw_id]?.doc_no || o.jw_id)}</span></div>
      </div>
      <table>
        <thead><tr><th style="width:32px">#</th><th>Bundle Code</th><th>Design</th><th>Shade</th><th style="text-align:right">Qty</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="5" style="text-align:center;color:#999">No bundles</td></tr>'}</tbody>
        <tfoot><tr><td colspan="4" style="text-align:right">Total (${(o.bundles || []).length} bundles)</td><td style="text-align:right">${o.sent_qty ?? 0}</td></tr></tfoot>
      </table>
      ${o.remarks ? `<p style="margin-top:10px"><span class="lbl">Remarks: </span>${esc(o.remarks)}</p>` : ''}
      <div class="sign"><div>Dispatched By</div><div>Jobber Signature</div></div>
      </body></html>`;
  };

  const printOrder = async (jwId) => {
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:Arial;padding:20px">Preparing dispatch slip…</p>');
    try {
      const data = await getJobOrder(jwId);
      const html = buildSlipHtml(data.order);
      if (win) { win.document.open(); win.document.write(html); win.document.close(); }
    } catch (err) {
      if (win) win.document.write('<p style="font-family:Arial;color:#b00;padding:20px">Failed to load job order.</p>');
      toast.error(err.response?.data?.detail || 'Failed to load job order for print');
    }
  };

  const submitForceClose = async () => {
    if (!fcTarget) return;
    if (!fcReason.trim()) { toast.error('Enter a reason'); return; }
    setFcSubmitting(true);
    try {
      await forceCloseJobOrder(fcTarget.jw_id, { reason: fcReason.trim() });
      toast.success(`${fcTarget.jw_id} force-closed (shortage ${fcTarget.balance_qty})`);
      setFcTarget(null);
      setFcReason('');
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to force-close');
    } finally {
      setFcSubmitting(false);
    }
  };

  const q = search.trim().toLowerCase();
  const visible = rows.filter((o) => {
    if (q && ![o.jw_id, o.jobber_name, o.design_name, o.shade, o.fg_shade, o.sku_code, o.sort_no, o.category2]
      .some((v) => (v || '').toString().toLowerCase().includes(q))) return false;
    const dk = dayKey(o.sent_date);
    if (dateFrom && (!dk || dk < dateFrom)) return false;
    if (dateTo && (!dk || dk > dateTo)) return false;
    return true;
  });

  const totals = visible.reduce((a, o) => ({
    sent: a.sent + (o.sent_qty || 0),
    good: a.good + (o.returned_good_qty || 0),
    damage: a.damage + (o.returned_damage_qty || 0),
    balance: a.balance + (o.balance_qty || 0),
  }), { sent: 0, good: 0, damage: 0, balance: 0 });

  const exportCsv = () => {
    if (!visible.length) { toast.error('Nothing to export'); return; }
    const headers = ['JW ID', 'Jobber', 'Design', 'Sort No', 'FG Shade', 'Category2',
      'Sent Date', 'Receipted Date', 'Bundles', 'Sent', 'Good', 'Damage', 'Balance', 'Status'];
    const cell = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = visible.map((o) => [
      o.jw_id, o.jobber_name, o.design_name, o.sort_no, o.fg_shade, o.category2,
      fmtDate(o.sent_date), fmtDate(o.receipted_date), o.bundle_count,
      o.sent_qty, o.returned_good_qty, o.returned_damage_qty, o.balance_qty, o.status,
    ].map(cell).join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jobber_report_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const canForceClose = (o) => admin && (o.balance_qty || 0) > 0
    && !['COMPLETED', 'CANCELLED', 'FORCE_CLOSED'].includes(o.status);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="jobber-report-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/inhouse')}
              className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <FileBarChart className="w-7 h-7" />
              <div>
                <h1 className="text-xl font-bold">Jobber Report</h1>
                <p className="text-sm text-[#E8DCC8]">Sent vs Good / Damage, balance & status</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate('/sections/inhouse/create-job-order')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="new-order-btn">
              <PackagePlus className="w-4 h-4 mr-2" /> New Order
            </Button>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-white/10 border-white/20 text-white" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="OPEN">Open</SelectItem>
                <SelectItem value="RECEIVING">Receiving</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="FORCE_CLOSED">Force Closed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchReport} disabled={loading}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 md:px-6 py-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-lg border p-3"><p className="text-xs text-gray-500">Total Sent</p><p className="text-2xl font-bold text-indigo-700" data-testid="total-sent">{totals.sent}</p></div>
          <div className="bg-white rounded-lg border p-3"><p className="text-xs text-gray-500">Good Returned</p><p className="text-2xl font-bold text-emerald-700" data-testid="total-good">{totals.good}</p></div>
          <div className="bg-white rounded-lg border p-3"><p className="text-xs text-gray-500">Damage Returned</p><p className="text-2xl font-bold text-amber-700" data-testid="total-damage">{totals.damage}</p></div>
          <div className="bg-white rounded-lg border p-3"><p className="text-xs text-gray-500">Balance</p><p className="text-2xl font-bold text-red-600" data-testid="total-balance">{totals.balance}</p></div>
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b bg-gray-50/60 flex flex-wrap items-end justify-between rounded-t-lg gap-3">
              <span className="text-sm font-medium text-gray-600 self-center" data-testid="report-count">
                {loading ? 'Loading…' : `${visible.length} job order${visible.length === 1 ? '' : 's'}`}
              </span>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-[11px] text-gray-500">Sent from</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="h-8 w-[150px]" data-testid="date-from" />
                </div>
                <div>
                  <Label className="text-[11px] text-gray-500">Sent to</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="h-8 w-[150px]" data-testid="date-to" />
                </div>
                {(dateFrom || dateTo) && (
                  <Button variant="ghost" size="sm" className="h-8 text-gray-500"
                    onClick={() => { setDateFrom(''); setDateTo(''); }} data-testid="clear-dates">Clear</Button>
                )}
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search JW / jobber / design / SKU / sort / category…"
                  className="w-[280px] h-8" data-testid="report-search" />
                <Button variant="outline" size="sm" className="h-8" onClick={exportCsv} data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-1.5" /> CSV
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="pl-4">JW ID</TableHead>
                    <TableHead>Jobber</TableHead>
                    <TableHead>Design</TableHead>
                    <TableHead>Sort No</TableHead>
                    <TableHead>FG Shade</TableHead>
                    <TableHead>Category2</TableHead>
                    <TableHead>Sent Date</TableHead>
                    <TableHead>Receipted Date</TableHead>
                    <TableHead className="text-right">Bundles</TableHead>
                    <TableHead className="text-right">Sent</TableHead>
                    <TableHead className="text-right">Good</TableHead>
                    <TableHead className="text-right">Damage</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>E-way bill</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead className="text-right pr-4">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={COL_COUNT} className="text-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
                    </TableCell></TableRow>
                  ) : visible.length === 0 ? (
                    <TableRow><TableCell colSpan={COL_COUNT} className="text-center py-8 text-gray-400" data-testid="empty-row">
                      No job orders found
                    </TableCell></TableRow>
                  ) : visible.map((o) => (
                    <TableRow key={o.jw_id} data-testid={`jr-row-${o.jw_id}`}>
                      <TableCell className="font-mono text-xs font-semibold pl-4">{o.jw_id}</TableCell>
                      <TableCell className="text-xs text-gray-600">{o.jobber_name || '—'}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate" title={o.design_name}>{o.design_name || '—'}</TableCell>
                      <TableCell className="text-xs text-gray-700 whitespace-nowrap" data-testid={`jr-sort-${o.jw_id}`}>{o.sort_no || '—'}</TableCell>
                      <TableCell className="text-xs text-gray-700">{o.fg_shade || '—'}</TableCell>
                      <TableCell className="text-xs text-gray-700 max-w-[140px] truncate" title={o.category2} data-testid={`jr-cat2-${o.jw_id}`}>{o.category2 || '—'}</TableCell>
                      <TableCell className="text-xs text-gray-600 whitespace-nowrap" data-testid={`jr-sent-date-${o.jw_id}`}>{fmtDate(o.sent_date)}</TableCell>
                      <TableCell className="text-xs text-gray-600 whitespace-nowrap" data-testid={`jr-recv-date-${o.jw_id}`}>{fmtDate(o.receipted_date)}</TableCell>
                      <TableCell className="text-right text-gray-600">{o.bundle_count}</TableCell>
                      <TableCell className="text-right font-semibold" data-testid={`jr-sent-${o.jw_id}`}>{o.sent_qty}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-700" data-testid={`jr-good-${o.jw_id}`}>{o.returned_good_qty}</TableCell>
                      <TableCell className="text-right font-semibold text-amber-700" data-testid={`jr-damage-${o.jw_id}`}>{o.returned_damage_qty}</TableCell>
                      <TableCell className={`text-right font-bold ${o.balance_qty === 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid={`jr-balance-${o.jw_id}`}>{o.balance_qty}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusClass(o.status)}>{o.status === 'FORCE_CLOSED' ? 'FORCE CLOSED' : o.status}</Badge>
                        {o.status === 'FORCE_CLOSED' && o.shortage_qty > 0 && (
                          <span className="block text-[10px] text-purple-600 mt-0.5" title={o.force_close_reason}>short {o.shortage_qty}</span>
                        )}
                      </TableCell>
                      <TableCell><JwEwbCell s={ewb[o.jw_id]} refNo={o.jw_id} testId={`jr-ewb-${o.jw_id}`} /></TableCell>
                      <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtAge(o.age_hours)}</span>
                      </TableCell>
                      <TableCell className="text-right pr-4">
                        <div className="flex items-center justify-end gap-1.5">
                          {canForceClose(o) && (
                            <Button variant="outline" size="sm" className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => { setFcTarget(o); setFcReason(''); }} data-testid={`force-close-${o.jw_id}`}>
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Close
                            </Button>
                          )}
                          <Button variant="outline" size="sm" className="h-8" onClick={() => setStickerJw(o.jw_id)} data-testid={`stickers-${o.jw_id}`}>
                            <Tag className="w-3.5 h-3.5 mr-1" /> USNs
                          </Button>
                          <Button variant="outline" size="sm" className="h-8" onClick={() => printOrder(o.jw_id)} data-testid={`print-slip-${o.jw_id}`}>
                            <Printer className="w-3.5 h-3.5 mr-1" /> Slip
                          </Button>
                          <JwTransferPrintBtn kind="job_order" refNo={o.jw_id} s={ewb[o.jw_id]} label="Transfer" testId={`jr-transfer-print-${o.jw_id}`} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>

      <UsnStickerDialog
        open={!!stickerJw}
        onOpenChange={(o) => { if (!o) setStickerJw(null); }}
        title={`USN Stickers · ${stickerJw || ''}`}
        screenKey="inhouse-job-order"
        fetchGroups={async () => { const d = await getBundleUsns(stickerJw); return d.groups || []; }}
      />

      <Dialog open={!!fcTarget} onOpenChange={(o) => { if (!o) { setFcTarget(null); setFcReason(''); } }}>
        <DialogContent data-testid="force-close-dialog">
          <DialogHeader>
            <DialogTitle>Force close {fcTarget?.jw_id}</DialogTitle>
            <DialogDescription>
              This order has a shortage of <strong className="text-red-600">{fcTarget?.balance_qty} piece(s)</strong> the
              jobber has not returned. Force-closing marks the order as closed with this shortage accepted.
              It can no longer receive returns. This action is admin-only and logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="fc-reason">Reason (required)</Label>
            <Textarea id="fc-reason" value={fcReason} onChange={(e) => setFcReason(e.target.value)}
              placeholder="e.g. Jobber confirmed pieces lost in processing / written off"
              rows={3} data-testid="force-close-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFcTarget(null); setFcReason(''); }} data-testid="force-close-cancel">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!fcReason.trim() || fcSubmitting}
              onClick={submitForceClose} data-testid="force-close-confirm">
              {fcSubmitting ? 'Closing…' : 'Force Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobberReport;
